// src/api/centralSuiteApi.ts
// Browser-direct fetch to Central's suite switch-context endpoint.
// NOT RTK Query. NOT proxied through CRM backend.
// Central is the sole authority for boot context.

import { decodeJwtPayload } from '@levelup/central-auth';
import { CENTRAL_URL, APP_ID, BEARER_ENDPOINTS } from '../config/centralAuth';
import type {
  SwitchContextWorkspace,
  SuiteAppExtended,
  SwitchContextUser,
  SwitchContextMeta,
} from '../store/slices/bootContextSlice';
import { updateBootContext } from '../store/slices/bootContextSlice';
import type { AppDispatch } from '../store/store';

// ============================================================
// Response type
// ============================================================

export interface SwitchContextResponse {
  currentWorkspace: SwitchContextWorkspace;
  otherWorkspaces: SwitchContextWorkspace[];
  /**
   * Apps include the optional `launchUrl` field from Central PR #193 when
   * provided; ui-shell 1.2.2+ uses it via `app.launchUrl ?? app.webUrl`.
   */
  apps: SuiteAppWithLaunch[];
  user: SwitchContextUser;
  meta?: SwitchContextMeta;
}

// ============================================================
// Error types
// ============================================================

export class WorkspaceClosedError extends Error {
  public readonly otherWorkspaces: SwitchContextWorkspace[];

  constructor(otherWorkspaces: SwitchContextWorkspace[]) {
    super('Workspace is closed');
    this.name = 'WorkspaceClosedError';
    this.otherWorkspaces = otherWorkspaces;
  }
}

export class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized — Central rejected token');
    this.name = 'UnauthorizedError';
  }
}

export class CentralUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CentralUnavailableError';
  }
}

// ============================================================
// Response mapping — normalize Central's actual field names
// ============================================================

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Map a workspace object from Central's format to our SwitchContextWorkspace.
 * Handles both camelCase and snake_case field names.
 *
 * TRUST BOUNDARY: Roles must come from Central (JWT or API response).
 * Clients must NEVER assign roles locally — doing so would be a privilege
 * escalation vulnerability. If role is missing, callers should extract it
 * from the JWT token (authoritative source) before using the workspace.
 */
function mapWorkspace(ws: any): SwitchContextWorkspace | null {
  if (!ws || typeof ws !== 'object') return null;
  return {
    id: ws.id || '',
    name: ws.name || '',
    slug: ws.slug || '',
    type: ws.type || 'business',
    // CRITICAL: Do NOT default to 'member' — use the actual role from Central.
    // If missing, caller must extract from JWT or treat as unresolved.
    role: ws.role,
    isPrimary: ws.isPrimary ?? ws.is_primary ?? false,
    ...(ws.status ? { status: ws.status } : {}),
    // ============================================================
    // Authorization fields — REQUIRED for `useHasCapability` /
    // `useHasWorkspacePermission` to function. Without these the
    // shared `hasCapability(context, appId, domain, action)` call
    // falls through to scope-checking, and since Central no longer
    // ships scopes in v2.3, every capability check returns false.
    //
    // This was the root cause of the "Permission Required" empty
    // state on the /phone and /sms pages — Central was sending
    // capabilities correctly, but this mapper was silently
    // dropping them.
    // ============================================================
    ...(ws.capabilities ? { capabilities: ws.capabilities } : {}),
    ...(ws.permissions ? { permissions: ws.permissions } : {}),
    ...(Array.isArray(ws.scopes) ? { scopes: ws.scopes } : {}),
    // Preserve structured avatar from Central
    // Format: { type: 'image' | 'initials', imageUrl?: string, initials?: string }
    ...(ws.avatar ? { avatar: ws.avatar } : {}),
    // Direct avatar URL from Central (alternative to structured avatar)
    avatarUrl: ws.avatarUrl ?? ws.avatar_url ?? null,
    // Onboarding version from Central (v1 or v2)
    onboardingVersion: ws.onboardingVersion ?? ws.onboarding_version ?? null,
    // Workspace creation timestamp from Central
    createdAt: ws.createdAt ?? ws.created_at ?? null,
  };
}

/**
 * SuiteAppExtended augmented with the optional `launchUrl` field that
 * `@bb-dev123/ui-shell@1.2.2+` reads (via `resolveAppLaunchUrl`) to prefer a
 * workspace-aware launch URL over the static `webUrl`. The base `SuiteApp`
 * type in `@bb-dev123/central-auth@1.3.0` does not declare `launchUrl`, but
 * the shell duck-types it at runtime, so adding it as an extension here both
 * keeps callers typed and lets ui-shell pick it up.
 */
export type SuiteAppWithLaunch = SuiteAppExtended & { launchUrl?: string };

/**
 * Map an app object from Central's format to our SuiteAppExtended.
 *
 * CRITICAL: This function must preserve all fields from Central's payload.
 * Do NOT conditionally spread fields — use direct assignment to ensure
 * consistent object shape across all apps.
 *
 * Fields preserved:
 * - ui: Contains theme tokens (ui.theme) and subtitle (ui.subtitle)
 * - assets: Contains icon data (assets.icon.iconUrl)
 * - displayName: Preferred display name for UI
 * - launchUrl: Workspace-aware launch URL (consumed by ui-shell 1.2.2+ via
 *   `app.launchUrl ?? app.webUrl`). When Central returns it, the shell uses
 *   it for cross-app navigation; otherwise the static `webUrl` is used.
 */
export function mapApp(app: any): SuiteAppWithLaunch | null {
  if (!app || typeof app !== 'object') return null;

  // STRICT: Central uses appId. Do NOT fall back to app.id — fail closed.
  const appId = app.appId || app.app_id || '';
  if (!appId) {
    if (import.meta.env.DEV) {
      console.warn('[mapApp] app missing appId field — skipping (fail closed):', { id: app.id, name: app.name });
    }
    return null;
  }

  // DEBUG: Log icon data from Central (remove after staging investigation)
  console.log('[mapApp] Icon data for', appId, {
    iconUrl: app.iconUrl || app.icon_url,
    assetsIconUrl: app.assets?.icon?.iconUrl,
    hasAssets: !!app.assets,
    hasAssetsIcon: !!app.assets?.icon,
  });

  // Normalize displayName from camelCase or snake_case
  const displayName = app.displayName || app.display_name || undefined;

  // Normalize iconUrl from camelCase or snake_case
  const iconUrl = app.iconUrl || app.icon_url || undefined;

  // Normalize mobileScheme from camelCase or snake_case
  const mobileScheme = app.mobileScheme || app.mobile_scheme || undefined;

  // Normalize launchUrl from camelCase or snake_case. Central PR #193 added
  // this as a workspace-aware launch URL; ui-shell 1.2.2+ consumes it via
  // `app.launchUrl ?? app.webUrl`. Preserve as-is; do NOT fall back to
  // webUrl here — the shell does that fallback at click time so the typed
  // value distinguishes "Central did not provide" from "Central provided".
  const launchUrl =
    typeof app.launchUrl === 'string'
      ? app.launchUrl
      : typeof app.launch_url === 'string'
        ? app.launch_url
        : undefined;

  return {
    appId,
    name: displayName || app.name || '',
    // Central returns webUrl — use as-is, no client-side env resolution
    webUrl: app.webUrl || app.web_url || app.url || '',
    status: app.status ?? 'active',
    order: typeof app.order === 'number' ? app.order : 0,

    // === Optional base fields ===
    iconUrl,
    mobileScheme,

    // === Extended fields for Suite UI Shell ===
    // HARDENED: Use direct assignment, not conditional spreading.
    // This ensures consistent object shape and prevents silent field drops.
    displayName,

    // Preserve ui object directly from Central payload.
    // Contains: ui.theme (v1.1 tokens), ui.subtitle
    ui: app.ui,

    // Preserve assets object directly from Central payload.
    // Contains: assets.icon.iconUrl, assets.icon.iconKey, assets.icon.monochrome
    assets: app.assets,

    // ui-shell 1.2.2+ consumes this. Omitted from the object when Central
    // did not supply one (so `Object.hasOwn(app, 'launchUrl')` is a reliable
    // signal in tests and downstream consumers).
    ...(launchUrl !== undefined ? { launchUrl } : {}),
  };
}

/**
 * Map a user object from Central's format to our SwitchContextUser.
 * Preserves internal platform flags for dev/staging features (testing mode).
 */
function mapUser(u: any): SwitchContextUser | null {
  if (!u || typeof u !== 'object') return null;
  return {
    id: u.id || u.sub || '',
    email: u.email || '',
    fullName: u.fullName ?? u.full_name ?? u.name ?? null,
    avatarUrl: u.avatarUrl ?? u.avatar_url ?? null,
    // Preserve internal user flags from Central (for testing mode visibility)
    platformInternal: u.platformInternal ?? u.platform_internal ?? false,
    platformSuperAdmin: u.platformSuperAdmin ?? u.platform_super_admin ?? false,
  };
}

/**
 * Map Central's switch-context response to our expected interface.
 *
 * Central may return field names like:
 *   workspace / currentWorkspace → currentWorkspace
 *   workspaces / otherWorkspaces → otherWorkspaces
 *   applications / apps          → apps
 *   user / account               → user
 *
 * EXPORTED (H-2C2 WP-6): this is the single normalizer for BOTH boot paths —
 * the legacy Bearer-model `fetchSwitchContext` below and the OIDC RP path
 * (`RpBootGate` → `rpContextToBootReady`). Sharing it guarantees the two paths
 * hydrate `bootContext` identically — no divergence. It only renames/normalizes
 * Central's field names; it derives no apps, entitlements, roles, or
 * workspace validity (roles always come from Central — see `mapWorkspace`).
 */
export function mapSwitchContextResponse(data: any): SwitchContextResponse {
  // --- currentWorkspace ---
  const rawCurrentWs = data.currentWorkspace || data.workspace || data.current_workspace;
  const currentWorkspace = rawCurrentWs ? mapWorkspace(rawCurrentWs) : null;

  // --- otherWorkspaces ---
  const rawOtherWs = data.otherWorkspaces || data.workspaces || data.other_workspaces || [];
  const otherWorkspaces = (Array.isArray(rawOtherWs) ? rawOtherWs : [])
    .map(mapWorkspace)
    .filter((ws): ws is SwitchContextWorkspace => ws !== null);

  // --- apps ---
  const rawApps = data.apps || data.applications || [];
  const apps = (Array.isArray(rawApps) ? rawApps : [])
    .map(mapApp)
    .filter((app): app is SuiteAppExtended => app !== null);

  // --- user ---
  const rawUser = data.user || data.account;
  const user = rawUser ? mapUser(rawUser) : null;

  return {
    currentWorkspace: currentWorkspace!,
    otherWorkspaces,
    apps,
    user: user!,
    meta: data.meta,
  };
}

/* eslint-enable @typescript-eslint/no-explicit-any */

// ============================================================
// Select workspace response
// ============================================================

export interface SelectWorkspaceResponse {
  accessToken: string;
  /** New refresh token — Central may issue a new one scoped to the selected workspace */
  refreshToken: string | null;
  expiresIn: number;
  workspace: SwitchContextWorkspace;
}

// ============================================================
// Fetch functions
// ============================================================

const SWITCH_CONTEXT_TIMEOUT_MS = 10_000;

/**
 * Call Central's suite switch-context endpoint directly from the browser.
 *
 * GET {CENTRAL_URL}/api/suite/switch-context
 * Headers: Authorization: Bearer <accessToken>, X-App-Id: crm
 *
 * @throws {WorkspaceClosedError} on 409 — workspace is closed
 * @throws {UnauthorizedError} on 401 — token rejected
 * @throws {CentralUnavailableError} on network/timeout/malformed response
 */
export async function fetchSwitchContext(
  accessToken: string
): Promise<SwitchContextResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SWITCH_CONTEXT_TIMEOUT_MS);

  try {
    // The URL is safe to log. The TOKEN is not, and a prefix is not a
    // mitigation: `slice(0, 30)` of a Central access token is its full JWT
    // header plus the start of the payload, and for any opaque token format it
    // is 30 usable characters of a live credential sitting in a place users
    // paste into bug reports. Logged presence, never material.
    console.log('[fetchSwitchContext] Calling:', `${CENTRAL_URL}/api/suite/switch-context`);
    console.log('[fetchSwitchContext] Token present:', accessToken.length > 0);

    const response = await fetch(`${CENTRAL_URL}/api/suite/switch-context`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-App-Id': APP_ID,
      },
      signal: controller.signal,
    });

    console.log('[fetchSwitchContext] Response status:', response.status);

    if (response.status === 200) {
      const data = await response.json();

      // Map Central's actual response shape to our expected interface.
      // Central may return different field names than our frontend types expect.
      const mapped = mapSwitchContextResponse(data);

      // Validate required fields after mapping
      if (!mapped.currentWorkspace || !mapped.user) {
        throw new CentralUnavailableError('Malformed switch-context response');
      }

      return mapped;
    }

    if (response.status === 409) {
      const data = await response.json().catch(() => ({}));
      throw new WorkspaceClosedError(data.otherWorkspaces || []);
    }

    if (response.status === 401) {
      const body401 = await response.text().catch(() => '');
      console.error('[fetchSwitchContext] 401 response body:', body401);
      throw new UnauthorizedError();
    }

    // Any other status — treat as unavailable
    const unexpectedBody = await response.text().catch(() => '');
    console.error('[fetchSwitchContext] Unexpected status:', response.status, unexpectedBody);
    throw new CentralUnavailableError(
      `Central returned unexpected status ${response.status}`
    );
  } catch (err) {
    // Re-throw known errors
    if (
      err instanceof WorkspaceClosedError ||
      err instanceof UnauthorizedError ||
      err instanceof CentralUnavailableError
    ) {
      throw err;
    }

    // AbortController timeout
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CentralUnavailableError('Central switch-context request timed out');
    }

    // Network errors
    throw new CentralUnavailableError(
      `Failed to reach Central: ${err instanceof Error ? err.message : 'Unknown error'}`
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Select/switch workspace via Central's bearer-token endpoint.
 *
 * POST {CENTRAL_URL}/api/auth/select-workspace
 * Headers: Authorization: Bearer <accessToken>, Content-Type: application/json
 *
 * This is the official bearer-auth endpoint for workspace selection,
 * matching the server-side centralApiClient.ts contract.
 * Do NOT use /api/oidc/session/select-workspace — that requires session cookies.
 *
 * @throws {UnauthorizedError} on 401
 * @throws {CentralUnavailableError} on network/timeout/error
 */
export async function selectWorkspaceViaBearer(
  accessToken: string,
  workspaceId: string
): Promise<SelectWorkspaceResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SWITCH_CONTEXT_TIMEOUT_MS);

  try {
    const response = await fetch(BEARER_ENDPOINTS.selectWorkspace, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ workspaceId }),
      signal: controller.signal,
    });

    if (response.ok) {
      const data = await response.json();

      // Normalize response — Central may use camelCase or snake_case
      const newAccessToken = data.accessToken || data.access_token;
      const newRefreshToken = data.refreshToken || data.refresh_token || null;
      // Server-side contract returns expiresAt (ISO string), frontend expects expiresIn (seconds)
      let expiresIn = data.expiresIn ?? data.expires_in;
      if (!expiresIn && data.expiresAt) {
        expiresIn = Math.floor((new Date(data.expiresAt).getTime() - Date.now()) / 1000);
      }
      expiresIn = expiresIn ?? 3600;
      const rawWorkspace = data.workspace || data.current_workspace;

      if (!newAccessToken) {
        throw new CentralUnavailableError('No access token in select-workspace response');
      }

      // TRUST BOUNDARY: Roles must come from Central (JWT or API response).
      // The JWT token is the authoritative source for the user's role in this workspace.
      // Clients must NEVER assign roles locally — Central is the single source of truth.
      let roleFromJwt: string | undefined;
      try {
        const claims = decodeJwtPayload(newAccessToken);
        roleFromJwt = claims.role as string | undefined;
      } catch {
        // Ignore JWT decode errors — role will remain undefined
      }

      const mappedWorkspace = rawWorkspace ? mapWorkspace(rawWorkspace) : null;

      // Build workspace object. Role comes from:
      // 1. API response (via mapWorkspace) — if Central included it
      // 2. JWT claims — authoritative source if API didn't include role
      // 3. undefined — if neither source has role (UI should handle gracefully)
      const workspace: SwitchContextWorkspace = mappedWorkspace || {
        id: workspaceId,
        name: 'Workspace',
        slug: 'workspace',
        type: 'business' as const,
        role: roleFromJwt, // From JWT (authoritative), may be undefined
        isPrimary: false,
      };

      // If workspace came from API but role is missing, use JWT role (authoritative).
      if (!workspace.role && roleFromJwt) {
        workspace.role = roleFromJwt;
      }

      return {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        expiresIn,
        workspace,
      };
    }

    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    const errorData = await response.json().catch(() => ({}));
    throw new CentralUnavailableError(
      errorData.error || `Select workspace failed with status ${response.status}`
    );
  } catch (err) {
    if (err instanceof UnauthorizedError || err instanceof CentralUnavailableError) {
      throw err;
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CentralUnavailableError('Select workspace request timed out');
    }
    throw new CentralUnavailableError(
      `Failed to select workspace: ${err instanceof Error ? err.message : 'Unknown error'}`
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Create a new workspace via Central's bearer-token endpoint.
 *
 * POST {CENTRAL_URL}/api/workspaces
 * Headers: Authorization: Bearer <accessToken>, Content-Type: application/json
 *
 * This is the official bearer-auth endpoint for workspace creation,
 * matching the server-side centralApiClient.ts contract.
 * Do NOT use /api/oidc/session/workspaces — that requires session cookies.
 *
 * @throws {UnauthorizedError} on 401
 * @throws {CentralUnavailableError} on network/timeout/error
 */
export async function createWorkspaceViaBearer(
  accessToken: string,
  params: { name: string; type: string; industry?: string }
): Promise<{ workspace: SwitchContextWorkspace }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SWITCH_CONTEXT_TIMEOUT_MS);

  try {
    const response = await fetch(BEARER_ENDPOINTS.createWorkspace, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    });

    if (response.ok) {
      const data = await response.json();
      const rawWorkspace = data.workspace || data;
      const workspace = rawWorkspace
        ? mapWorkspace(rawWorkspace)
        : null;

      if (!workspace) {
        throw new CentralUnavailableError('No workspace in create-workspace response');
      }

      // TRUST BOUNDARY: Roles must come from Central (JWT or API response).
      // Clients must NEVER assign roles locally — doing so would be privilege escalation.
      // If Central's create response doesn't include role, extract from JWT (authoritative).
      if (!workspace.role) {
        try {
          const claims = decodeJwtPayload(accessToken);
          if (claims.role) {
            workspace.role = claims.role as string;
          }
        } catch {
          // JWT decode failed — role remains undefined, UI should handle gracefully
        }
      }

      return { workspace };
    }

    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    const errorData = await response.json().catch(() => ({}));
    throw new CentralUnavailableError(
      errorData.error || `Create workspace failed with status ${response.status}`
    );
  } catch (err) {
    if (err instanceof UnauthorizedError || err instanceof CentralUnavailableError) {
      throw err;
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CentralUnavailableError('Create workspace request timed out');
    }
    throw new CentralUnavailableError(
      `Failed to create workspace: ${err instanceof Error ? err.message : 'Unknown error'}`
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Update a workspace via Central's bearer-token endpoint.
 *
 * PATCH {CENTRAL_URL}/api/workspaces/:workspaceId
 * Headers: Authorization: Bearer <accessToken>, Content-Type: application/json
 *
 * Currently supports updating workspace name only.
 *
 * @throws {UnauthorizedError} on 401
 * @throws {CentralUnavailableError} on network/timeout/error
 * @throws {Error} on 403 (insufficient permissions) or 404 (not found)
 */
export async function updateWorkspaceViaBearer(
  accessToken: string,
  workspaceId: string,
  params: { name: string }
): Promise<SwitchContextWorkspace> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SWITCH_CONTEXT_TIMEOUT_MS);

  try {
    const response = await fetch(`${CENTRAL_URL}/api/workspaces/${workspaceId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    });

    if (response.ok) {
      const data = await response.json();
      const workspace = mapWorkspace(data.workspace);
      if (!workspace) {
        throw new CentralUnavailableError('No workspace in update response');
      }
      return workspace;
    }

    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    if (response.status === 403) {
      throw new Error('Insufficient permissions to update workspace');
    }

    if (response.status === 404) {
      throw new Error('Workspace not found');
    }

    const errorData = await response.json().catch(() => ({}));
    throw new CentralUnavailableError(
      errorData.error || `Update workspace failed with status ${response.status}`
    );
  } catch (err) {
    if (
      err instanceof UnauthorizedError ||
      err instanceof CentralUnavailableError ||
      (err instanceof Error && (err.message.includes('permissions') || err.message.includes('not found')))
    ) {
      throw err;
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CentralUnavailableError('Update workspace request timed out');
    }
    throw new CentralUnavailableError(
      `Failed to update workspace: ${err instanceof Error ? err.message : 'Unknown error'}`
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch workspace details from Central.
 *
 * GET {CENTRAL_URL}/api/workspaces/:workspaceId
 * Headers: Authorization: Bearer <accessToken>
 *
 * Returns full workspace details including:
 * - id, name, slug, type, status
 * - avatarUrl, onboardingVersion, createdAt
 *
 * @throws {UnauthorizedError} on 401
 * @throws {CentralUnavailableError} on network/timeout/error
 * @throws {Error} on 404 (not found)
 */
export async function fetchWorkspaceDetails(
  accessToken: string,
  workspaceId: string
): Promise<SwitchContextWorkspace> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SWITCH_CONTEXT_TIMEOUT_MS);

  try {
    const response = await fetch(`${CENTRAL_URL}/api/workspaces/${workspaceId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal: controller.signal,
    });

    if (response.ok) {
      const data = await response.json();
      const workspace = mapWorkspace(data.workspace || data);
      if (!workspace) {
        throw new CentralUnavailableError('No workspace in response');
      }
      return workspace;
    }

    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    if (response.status === 404) {
      throw new Error('Workspace not found');
    }

    const errorData = await response.json().catch(() => ({}));
    throw new CentralUnavailableError(
      errorData.error || `Fetch workspace failed with status ${response.status}`
    );
  } catch (err) {
    if (
      err instanceof UnauthorizedError ||
      err instanceof CentralUnavailableError ||
      (err instanceof Error && err.message.includes('not found'))
    ) {
      throw err;
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CentralUnavailableError('Fetch workspace request timed out');
    }
    throw new CentralUnavailableError(
      `Failed to fetch workspace: ${err instanceof Error ? err.message : 'Unknown error'}`
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Response from Central's presigned upload URL endpoint.
 */
export interface PresignedUploadResponse {
  uploadUrl: string;
  fileUrl: string;
  expiresIn: number;
}

/**
 * Get a presigned URL for uploading a workspace avatar.
 *
 * POST {CENTRAL_URL}/api/workspaces/:workspaceId/avatar/presign
 * Headers: Authorization: Bearer <accessToken>
 * Body: { contentType: string, fileName: string }
 *
 * @returns Presigned upload URL and final file URL
 */
export async function getWorkspaceAvatarPresignedUrl(
  accessToken: string,
  workspaceId: string,
  contentType: string,
  fileName: string
): Promise<PresignedUploadResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SWITCH_CONTEXT_TIMEOUT_MS);

  try {
    const response = await fetch(`${CENTRAL_URL}/api/workspaces/${workspaceId}/avatar/presign`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ contentType, fileName }),
      signal: controller.signal,
    });

    if (response.ok) {
      return await response.json();
    }

    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    if (response.status === 403) {
      throw new Error('Insufficient permissions to upload avatar');
    }

    const errorData = await response.json().catch(() => ({}));
    throw new CentralUnavailableError(
      errorData.error || `Get presigned URL failed with status ${response.status}`
    );
  } catch (err) {
    if (
      err instanceof UnauthorizedError ||
      err instanceof CentralUnavailableError ||
      (err instanceof Error && err.message.includes('permissions'))
    ) {
      throw err;
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CentralUnavailableError('Presigned URL request timed out');
    }
    throw new CentralUnavailableError(
      `Failed to get presigned URL: ${err instanceof Error ? err.message : 'Unknown error'}`
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Update workspace avatar URL after successful upload.
 *
 * PATCH {CENTRAL_URL}/api/workspaces/:workspaceId
 * Headers: Authorization: Bearer <accessToken>
 * Body: { avatarUrl: string }
 */
export async function updateWorkspaceAvatar(
  accessToken: string,
  workspaceId: string,
  avatarUrl: string | null
): Promise<SwitchContextWorkspace> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SWITCH_CONTEXT_TIMEOUT_MS);

  try {
    const response = await fetch(`${CENTRAL_URL}/api/workspaces/${workspaceId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ avatarUrl }),
      signal: controller.signal,
    });

    if (response.ok) {
      const data = await response.json();
      const workspace = mapWorkspace(data.workspace);
      if (!workspace) {
        throw new CentralUnavailableError('No workspace in update response');
      }
      return workspace;
    }

    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    if (response.status === 403) {
      throw new Error('Insufficient permissions to update avatar');
    }

    const errorData = await response.json().catch(() => ({}));
    throw new CentralUnavailableError(
      errorData.error || `Update avatar failed with status ${response.status}`
    );
  } catch (err) {
    if (
      err instanceof UnauthorizedError ||
      err instanceof CentralUnavailableError ||
      (err instanceof Error && err.message.includes('permissions'))
    ) {
      throw err;
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CentralUnavailableError('Update avatar request timed out');
    }
    throw new CentralUnavailableError(
      `Failed to update avatar: ${err instanceof Error ? err.message : 'Unknown error'}`
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

// ============================================================
// Background Refetch Helper
// ============================================================

/**
 * Refetch switch-context without blocking boot.
 * Used for background refresh after SSE events or reconnect.
 *
 * INVARIANT: Does NOT set bootStatus to 'loading' to avoid UI blocking/boot loops.
 * Uses updateBootContext to update state while keeping bootStatus as 'ready'.
 *
 * @param accessToken - Central access token
 * @param dispatch - Redux dispatch function
 */
export async function refetchSwitchContext(
  accessToken: string,
  dispatch: AppDispatch
): Promise<void> {
  try {
    const response = await fetchSwitchContext(accessToken);
    dispatch(updateBootContext(response));
  } catch (error) {
    // Log but don't throw — this is a background refresh
    // Boot remains ready, user can continue working
    console.error('[refetchSwitchContext] Background refresh failed:', error);
  }
}
